import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Building2, MapPin, Info, ArrowUpRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import { api } from '../api';

function fmtInt(n) { if (n == null) return '—'; return Number(n).toLocaleString(); }

function Stat({ value, label, accent }) {
  return (
    <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
      <div className={`font-display text-2xl tracking-tight ${accent || 'text-[#1A2622]'}`}>{value}</div>
      <div className="mt-1 text-[10.5px] uppercase tracking-wider text-[#7A857F]">{label}</div>
    </div>
  );
}

export default function MAPayerDetailPage() {
  const { org } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.maPayer(org).then(setP).catch(e => setErr(e?.response?.data?.detail || 'Payer not found')).finally(() => setLoading(false));
  }, [org]);

  if (loading) return <div className="min-h-screen"><Navbar/><main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-10"><div className="text-sm text-[#7A857F]">Loading payer…</div></main></div>;
  if (err || !p) return <div className="min-h-screen"><Navbar/><main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-10"><h1 className="font-display text-3xl">Payer not found</h1><p className="mt-3 text-[#7A857F]">{err}</p></main></div>;

  const topState = (p.by_state || [])[0];

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-12">
        <Breadcrumb items={[
          { label: 'Home', to: '/' },
          { label: 'Managed Care', to: '/managed-care' },
          { label: 'Payers', to: '/managed-care/payers' },
          { label: p.organization_name_display },
        ]} />

        <div className="mt-6">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#4A7C5E]/30 bg-white">
            <Building2 size={12} className="text-[#4A7C5E]"/>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#4A7C5E]">MA payer · {p.contract_count} active contracts</span>
          </div>
          <h1 className="mt-3 font-display text-4xl md:text-5xl font-semibold text-[#1A2622] tracking-tight leading-tight">
            {p.organization_name_display}
          </h1>
        </div>

        <section className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat value={fmtInt(p.total_enrolled)} label="Total MA enrolled" accent="text-[#4A7C5E]" />
          <Stat value={fmtInt(p.counties_served)} label="Counties served" />
          <Stat value={fmtInt(p.states_served)} label="States served" />
          <Stat value={topState ? `${topState.state_code}` : '—'} label="Largest state"
                accent="text-[#1A2622]" />
        </section>

        <section className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#F0E7D2] flex items-center gap-2">
              <MapPin size={14} className="text-[#4A7C5E]"/>
              <h2 className="font-display text-base font-semibold text-[#1A2622]">Top states by enrollment</h2>
            </div>
            <ul>
              {(p.by_state || []).slice(0, 15).map((s, i) => (
                <li key={s.state_code} className="px-4 py-2.5 flex items-center justify-between border-t border-[#F0E7D2] first:border-t-0">
                  <Link to={`/managed-care/state/${s.state_code}`} className="text-[12.5px] text-[#1A2622] font-medium hover:text-[#2D5F4F] flex items-center gap-2">
                    <span className="text-[10.5px] text-[#7A857F] w-5">{i + 1}</span>
                    {s.state_code}
                    <ArrowUpRight size={11} className="text-[#A8B0AA]" />
                  </Link>
                  <span className="text-[12.5px] font-semibold tabular-nums">{fmtInt(s.enrolled)}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#F0E7D2] flex items-center gap-2">
              <h2 className="font-display text-base font-semibold text-[#1A2622]">Top counties</h2>
              <span className="ml-auto text-[10.5px] uppercase tracking-wider text-[#7A857F]">by single-county enrollment</span>
            </div>
            <ul>
              {(p.top_counties || []).slice(0, 15).map((c, i) => (
                <li key={`${c.fips}-${c.contract_id}-${i}`} className="px-4 py-2.5 flex items-center justify-between border-t border-[#F0E7D2] first:border-t-0">
                  <div className="min-w-0">
                    <div className="text-[12.5px] text-[#1A2622] font-medium truncate">{c.county_name}, {c.state_code}</div>
                    <div className="text-[10.5px] text-[#7A857F]">{c.plan_type} · contract {c.contract_id}</div>
                  </div>
                  <span className="text-[12.5px] font-semibold tabular-nums">{fmtInt(c.enrolled)}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mt-4 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
          <div className="px-5 py-4 border-b border-[#F0E7D2]">
            <h2 className="font-display text-base font-semibold text-[#1A2622]">Plan-type mix (enrollment)</h2>
          </div>
          <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
            {(p.by_plan_type || []).map((pt) => (
              <div key={pt.plan_type} className="p-3 rounded-lg bg-[#FBF6EC] border border-[#F0E7D2]">
                <div className="text-[12.5px] font-semibold text-[#1A2622]">{pt.plan_type}</div>
                <div className="text-[11px] text-[#7A857F] mt-0.5">{fmtInt(pt.enrolled)} enrolled</div>
              </div>
            ))}
          </div>
        </section>

        <div className="mt-5 flex items-start gap-2 text-[11.5px] text-[#A8B0AA]">
          <Info size={12} className="mt-0.5 shrink-0"/>
          <span>Source: {p.source}. Enrollment counts ≤ 10 are excluded from the source file (HIPAA suppression).</span>
        </div>
      </main>
      <Footer />
    </div>
  );
}
